import { graphql } from './api';

/** Comments inbox (MVP_PHASES.md 7d, Pro and Plus): shoppers' comments on the store's own posts. Every write here
 * is a merchant click; the API never replies or hides on its own. */

export type CommentTab = 'questions' | 'all' | 'hidden';
export type CommentIntent = 'question' | 'spam' | 'other';
export type CommentProduct = { title: string; url: string };
export type CommentReply = { id: string; username: string; text: string; commentedAt: string; isFromAccount: boolean; isHidden: boolean };
export type Comment = {
  id: string;
  username: string;
  text: string;
  commentedAt: string;
  likeCount: number;
  isHidden: boolean;
  intent: CommentIntent;
  status: 'open' | 'replied' | 'dismissed';
  repliedAt: string | null;
  post: { id: string; caption: string; permalink: string; thumbnailUrl: string } | null;
  products: CommentProduct[];
  replies: CommentReply[];
  suggestedReply: string;
};
export type CommentsInfo = { available: boolean; allowed: boolean; scopeGranted: boolean; questions: number };
export type SavedReply = { id: string; name: string; body: string; intent: CommentIntent };
type UserError = { field: string; message: string };
type Result = { comment: Comment | null; userErrors: UserError[] };

const FIELDS = `id username text commentedAt likeCount isHidden intent status repliedAt
  post { id caption permalink thumbnailUrl } products { title url }
  replies { id username text commentedAt isFromAccount isHidden } suggestedReply`;
const RESULT = `comment { ${FIELDS} } userErrors { field message }`;

/** Thrown when the API refuses an action with a message for the merchant (e.g. "reconnect Instagram"). */
export class CommentError extends Error {}

function unwrap(result: Result): Comment | null {
  if (result.userErrors.length) throw new CommentError(result.userErrors[0].message);
  return result.comment;
}

export function loadInfo() {
  return graphql<{ commentsInfo: CommentsInfo }>(`{ commentsInfo { available allowed scopeGranted questions } }`).then(d => d.commentsInfo);
}

export function loadComments(tab: CommentTab) {
  return graphql<{ comments: Comment[] }>(`query Comments($tab: String!) { comments(tab: $tab) { ${FIELDS} } }`, { tab }).then(d => d.comments);
}

export function loadSavedReplies() {
  return graphql<{ savedReplies: SavedReply[] }>(`{ savedReplies { id name body intent } }`).then(d => d.savedReplies);
}

export async function reply(id: string, message: string): Promise<Comment | null> {
  const data = await graphql<{ replyToComment: Result }>(
    `mutation Reply($id: ID!, $message: String!) { replyToComment(id: $id, message: $message) { ${RESULT} } }`,
    { id, message },
  );
  return unwrap(data.replyToComment);
}

export async function setHidden(id: string, hidden: boolean): Promise<Comment | null> {
  const data = await graphql<{ setCommentHidden: Result }>(
    `mutation Hide($id: ID!, $hidden: Boolean!) { setCommentHidden(id: $id, hidden: $hidden) { ${RESULT} } }`,
    { id, hidden },
  );
  return unwrap(data.setCommentHidden);
}

export async function hideMany(ids: string[]): Promise<number> {
  const data = await graphql<{ hideComments: number }>(`mutation HideMany($ids: [ID!]!) { hideComments(ids: $ids) }`, { ids });
  return data.hideComments;
}

export async function setDismissed(id: string, dismissed: boolean): Promise<Comment | null> {
  const data = await graphql<{ setCommentDismissed: Result }>(
    `mutation Dismiss($id: ID!, $dismissed: Boolean!) { setCommentDismissed(id: $id, dismissed: $dismissed) { ${RESULT} } }`,
    { id, dismissed },
  );
  return unwrap(data.setCommentDismissed);
}

export async function remove(id: string): Promise<void> {
  const data = await graphql<{ deleteComment: Result }>(`mutation Delete($id: ID!) { deleteComment(id: $id) { userErrors { field message } } }`, { id });
  if (data.deleteComment.userErrors.length) throw new CommentError(data.deleteComment.userErrors[0].message);
}

/** Fill a saved reply the way the API does (lib/comments.render): used when the merchant picks another reply. */
export function fill(body: string, comment: Pick<Comment, 'username' | 'products'>): string {
  const product = comment.products[0];
  return body
    .replace('@{username}', comment.username ? `@${comment.username}` : 'there')
    .replaceAll('{username}', comment.username || 'there')
    .replaceAll('{product_title}', product?.title ?? 'it')
    .replaceAll('{product_url}', product?.url ?? '')
    .replace(/[ \t]{2,}/g, ' ')
    .trim()
    .replace(/:$/, '')
    .trim();
}
